/* eslint-disable camelcase */
import React from 'react';
import { FlexboxContainer, FlexboxItem, Table, Typography, TextLabel } from 'ui-components';
import { LiMultiterm } from 'lisecutilityfunctions/lib/utilitylib';
import { ownHardlockHeaderData } from '../../constants/HeaderData';
import { useAppSelector, useAppDispatch } from '../../../../hooks';
import IconUpdate from '../../../../assets/Icons/IconUpdate.svg';
import IconDownload from '../../../../assets/Icons/IconDownload.svg';
import { getVersions } from '../../../../api/versionNegotiationHelpers';
import { CM_CLIENT_v2_2 } from '../../../../utils/appConstants';
import { getMultiTermText } from '../../../../utils/commonUtils';

import { patchHardlockProduceCurrentUserEntitlement, getLatestV2CDownloadCurrentUser } from '../../slice/index';

interface IOwnHardlockTable {
    downloadedHardlockCallback: (hardlockId: any) => void;
}
// eslint-disable-next-line @typescript-eslint/no-shadow
const OwnHardlockTable: React.FC<IOwnHardlockTable> = ({ downloadedHardlockCallback }) => {
    const dispatch = useAppDispatch();

    const { hardlocksCurrentUserData } = useAppSelector((state) => state.hardlocks);

    /**
     * @function onOwnHardlockUpdate
     * @description function to call PATCH api for hardlock update.
     */
    const onOwnHardlockUpdate = (hardlockId: any) => {
        downloadedHardlockCallback(hardlockId);
        dispatch(patchHardlockProduceCurrentUserEntitlement({ hardlockId }));
    };

    /**
     * @function onOwnHardlockDownload
     * @description function to call api endpoint for hardlock download (.v2c file download part handled in hardlocks.tsx).
     */
    const onOwnHardlockDownload = async (hardlockId: number) => {
        const clientFileVersion = await getVersions();
        if (clientFileVersion >= CM_CLIENT_v2_2) {
            downloadedHardlockCallback(hardlockId);
            dispatch(getLatestV2CDownloadCurrentUser(hardlockId));
        }
    };

    /**
     * @function checkIfv2cExpired
     * @description function to check if the individual hardlock is expired based on the 'currentV2CExpiry' field.
     * @description comparison to 'today`s' is made to return a boolean value.
     */
    const checkIfv2cExpired = (currentV2CExpiry: string) => {
        if (currentV2CExpiry) {
            return new Date(currentV2CExpiry) < new Date();
        }
        return true;
    };

    /**
     * @function getActionIcon
     * @description function to return UPDATE | DOWNLOAD icon with it's respective action method
     */
    // eslint-disable-next-line max-len
    const getActionIcon = (hardlockId: number, currentV2CExpiry?: string) => {
        const isV2cExpired = checkIfv2cExpired(currentV2CExpiry || '');
        return (
            <FlexboxContainer
                alignItems="center"
                justifyContent="flex-start"
                onClick={() => (isV2cExpired ? onOwnHardlockUpdate(hardlockId) : onOwnHardlockDownload(hardlockId))}
            >
                <img
                    alt="alert-icon"
                    src={isV2cExpired ? IconUpdate : IconDownload}
                    style={{ height: '20px', width: '20px', position: 'absolute', margin: '6px 4px 2px 6px' }}
                />
                <TextLabel
                    className='Status_blue'
                    text={
                        isV2cExpired
                            ? getMultiTermText({ text: 'Update hardlock key', id: '99036003' })
                            : getMultiTermText({ text: 'Download hardlock key', id: '99036004' })
                    }
                />
            </FlexboxContainer>
        );
    };

    const getRowData = () =>
        hardlocksCurrentUserData?.map((eachData) => ({
            _key: eachData.id,
            id: eachData.id,
            lisecSerialNo: eachData.lisecSerialNo,
            typeName: eachData.typeName,
            action: getActionIcon(eachData.id, eachData?.currentV2CExpiry)
        }));

    return (
        <>
            <FlexboxItem className="Full_width TableHeading_wrapper">
                <Typography type="H1">
                    <LiMultiterm textId="99034912" textVal="My hardlocks" />
                </Typography>
            </FlexboxItem>
            <FlexboxItem className="Full_width Main_Table_wrapper My_Hardlock_Table">
                <Table
                    isManualSortEnabled
                    headerData={ownHardlockHeaderData}
                    isMultiSelectable={false}
                    isSingleSelectable={false}
                    rowsData={getRowData()}
                    tableHeaderRowStyles={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1
                    }}
                    tableConfiguration={{
                        pagination: true,
                        pageOptions: [20, 50, 100, 200],
                        showTableSettings: true,
                        showSearch: true,
                        sortAllRows: true,
                        showRowCount: true,
                        showExportButton: true
                    }}
                    pagination
                    tableId="ownHardlockTable"
                />
            </FlexboxItem>
        </>
    );
};

export default OwnHardlockTable;
